import { Col, Container, Nav, Navbar, Row } from 'react-bootstrap';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Auth } from 'aws-amplify';
import GetStartedCTA from '../GetStartedCTA';
import PropTypes from 'prop-types';
import cx from 'classnames';
import greenCurve from '../../assets/svg/green-curve.svg';
import greenCurveSm from '../../assets/svg/green-curve-sm.svg';
import logoWhite from '../../assets/svg/loop-logo-white.svg';
import purpleCurve from '../../assets/svg/purple-curve.svg';
import purpleCurveSm from '../../assets/svg/purple-curve-sm.svg';
import { signOut } from '../../utils/auth';
import styled from 'styled-components';
import yellowCurve from '../../assets/svg/yellow-curve.svg';
import yellowCurveSm from '../../assets/svg/yellow-curve-sm.svg';

const StyledContainer = styled(Container)`
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;
  background-color: #f5f5f5;
  z-index: 100;
`;

const StyledNavbar = styled(Navbar)`
  padding-top: 2px;
`;

const ContentDiv = styled.div`
  z-index: 0;
`;

const CopyrightDiv = styled.div`
  font-weight: bold;
  color: #fff;
  font-size: 14px;
`;

const BackgroundShapePurple = styled(StyledContainer)`
  background-image: url(${purpleCurve});

  @media (max-width: 1024px) {
    background-image: url(${purpleCurveSm});
  }
`;

const BackgroundShapeGreen = styled(StyledContainer)`
  background-image: url(${greenCurve});

  @media (max-width: 1024px) {
    background-image: url(${greenCurveSm});
  }
`;

const BackgroundShapeYellow = styled(StyledContainer)`
  background-image: url(${yellowCurve});

  @media (max-width: 1024px) {
    background-image: url(${yellowCurveSm});
  }
`;

const getBackgroundShape = backgroundTint => {
  switch (backgroundTint) {
    case 'green':
      return BackgroundShapeGreen;
    case 'yellow':
      return BackgroundShapeYellow;
    default:
      return BackgroundShapePurple;
  }
};

const OnboardingLayout = ({
  children,
  hideSupport,
  backgroundTint,
  showGetStarted,
  showNetZeroSupport,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const BackgroundShapeDiv = getBackgroundShape(backgroundTint);
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const asyncUseEffectMethod = async () => {
      try {
        setUser(await Auth.currentAuthenticatedUser());
      } catch {
        setUser(undefined);
      }
    };
    asyncUseEffectMethod().then(r => {});
  }, []);

  return (
    <BackgroundShapeDiv
      fluid
      className="d-flex flex-column align-content-between justify-content-between login-background p-0 onboarding">
      <div>
        <Container fluid>
          <Row>
            <Col className="p-0 onboarding-nav-container">
              <StyledNavbar collapseOnSelect expand="lg" variant="dark">
                <Navbar.Brand>
                  <Nav.Link href="/">
                    <img src={logoWhite} alt="Loop Energy" />
                  </Nav.Link>
                </Navbar.Brand>
                <Nav className="ml-auto">
                  {!!user && (
                    <Nav.Link href="/" onClick={() => signOut(navigate)}>
                      Sign Out
                    </Nav.Link>
                  )}
                </Nav>
              </StyledNavbar>
            </Col>
          </Row>
        </Container>
      </div>
      <div className="flex-fill">
        <Container fluid="lg">
          <Row>
            <Col>
              <ContentDiv className="d-flex justify-content-center p-sm-3 p-xs-1 pt-3">
                {children}
              </ContentDiv>
            </Col>
          </Row>
        </Container>
      </div>
      {showGetStarted && (
        <Row className="d-flex justify-content-center p-0 app-cta-space appstore-footer mt-5">
          {<GetStartedCTA navigate={navigate} />}
        </Row>
      )}
      <Navbar
        bg="dark"
        variant="dark"
        className={cx({ 'pr-0': true, 'mt-5': !showGetStarted })}>
        <Container fluid="lg">
          <Row className="w-100 py-lg-4 py-md-3">
            <Col>
              <CopyrightDiv className="pl-0 p-2">
                &copy; {new Date().getFullYear()} Loop. All Rights Reserved.
              </CopyrightDiv>
            </Col>
            <Col>
              <Nav
                className="d-flex justify-content-end p-0"
                activeKey={location.pathname}>
                <Nav.Link href="https://loop.homes/policies/">
                  Policies
                </Nav.Link>
                {hideSupport || (
                  <Nav.Link
                    href="https://kb.loop.homes?utm_source=loop&utm_medium=webapp"
                    className="pr-0">
                    Support
                  </Nav.Link>
                )}
                {showNetZeroSupport && (
                  <Nav.Link
                    href="https://kb.loop.homes/start-your-journey-to-net-zero-with-loop"
                    className="pr-0">
                    Support
                  </Nav.Link>
                )}
              </Nav>
            </Col>
          </Row>
        </Container>
      </Navbar>
    </BackgroundShapeDiv>
  );
};

OnboardingLayout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  hideSupport: PropTypes.bool,
  backgroundTint: PropTypes.oneOf(['green', 'purple', 'yellow']),
  showGetStarted: PropTypes.bool,
  showNetZeroSupport: PropTypes.bool,
};

export default OnboardingLayout;
